import React, { useCallback, useEffect, useState } from 'react';
import { Box, Chip, Stack, Tab, Tabs, Typography } from '@mui/material';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import AnimatedPage from '@/components/AnimatedPage';
import { canAccessPage, PageKey } from '@/auth/accessMatrix';
import { RootState } from '@/store';
import { payoutsService } from '@/services';

const PAYOUTS_OUT_INVOICES = '/payouts-out/invoices';
const PAYOUTS_OUT_CONFIRMATION = '/payouts-out/confirmation';
const PAYOUTS_OUT_CANCELLED = '/payouts-out/cancelled';
const PAYOUTS_OUT_STATISTICS = '/payouts-out/statistics';
const COUNTS_REFRESH_INTERVAL_MS = 30_000;

type PayoutTabCounts = {
  invoices: number;
  confirmation: number;
  cancelled: number;
};

const EMPTY_COUNTS: PayoutTabCounts = {
  invoices: 0,
  confirmation: 0,
  cancelled: 0,
};

const tabLabel = (label: string, count: number) => (
  <Stack direction="row" spacing={1} alignItems="center">
    <span>{label}</span>
    <Chip label={count} size="small" color="primary" variant="outlined" />
  </Stack>
);

function payoutsOutTabPath(pathname: string): string {
  if (pathname.includes('/invoices')) return PAYOUTS_OUT_INVOICES;
  if (pathname.includes('/confirmation')) return PAYOUTS_OUT_CONFIRMATION;
  if (pathname.includes('/cancelled')) return PAYOUTS_OUT_CANCELLED;
  return PAYOUTS_OUT_STATISTICS;
}

const PayoutsOutLayout: React.FC = () => {
  const { pathname } = useLocation();
  const user = useSelector((state: RootState) => state.auth.user);
  const tabValue = payoutsOutTabPath(pathname);
  const canAccessOperations = canAccessPage(user, PageKey.PAYOUTS_OUT_OPERATIONS);
  const [counts, setCounts] = useState<PayoutTabCounts>(EMPTY_COUNTS);

  const loadCounts = useCallback(async () => {
    if (!canAccessOperations) return;
    try {
      const response = await payoutsService.getPayoutInvoiceStatusCounts();
      setCounts({
        invoices: (response.counts.AVAILABLE ?? 0) + (response.counts.ACCEPTED ?? 0),
        confirmation: response.counts.IN_PROGRESS ?? 0,
        cancelled: response.counts.UNDER_REVIEW ?? 0,
      });
    } catch (error) {
      console.error('Failed to load payout status counts', error);
    }
  }, [canAccessOperations]);

  useEffect(() => {
    loadCounts();
    const intervalId = window.setInterval(loadCounts, COUNTS_REFRESH_INTERVAL_MS);
    return () => window.clearInterval(intervalId);
  }, [loadCounts, pathname]);

  return (
    <AnimatedPage>
      <Box>
        <Typography variant="h4" sx={{ fontWeight: 600, color: 'text.primary', mb: 2 }}>
          Выплаты (OUT)
        </Typography>
        <Tabs
          value={tabValue}
          sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
          aria-label="Разделы выплат OUT"
        >
          {canAccessOperations && (
            <Tab
              label={tabLabel('Счета', counts.invoices)}
              value={PAYOUTS_OUT_INVOICES}
              component={Link}
              to={PAYOUTS_OUT_INVOICES}
            />
          )}
          {canAccessOperations && (
            <Tab
              label={tabLabel('Подтверждение', counts.confirmation)}
              value={PAYOUTS_OUT_CONFIRMATION}
              component={Link}
              to={PAYOUTS_OUT_CONFIRMATION}
            />
          )}
          {canAccessOperations && (
            <Tab
              label={tabLabel('Отменённые', counts.cancelled)}
              value={PAYOUTS_OUT_CANCELLED}
              component={Link}
              to={PAYOUTS_OUT_CANCELLED}
            />
          )}
          <Tab
            label="Статистика"
            value={PAYOUTS_OUT_STATISTICS}
            component={Link}
            to={PAYOUTS_OUT_STATISTICS}
          />
        </Tabs>
        <Outlet />
      </Box>
    </AnimatedPage>
  );
};

export default PayoutsOutLayout;
